import type { TonyteActivity } from '../types';
import { SUPPORTED_CITIES, normalizeCityKey } from './cities';
import { isEventAvailableForDiscovery } from './eventAvailability';
import { parseActivityDate } from './activityDates';
import { isStudiZoneActivity } from './studiZone';

// =============================================================================
// In-app promotion management (admin/co-admin) — the mobile counterpart of
// Tonyte_website/src/promotionsModel.js. Same publication model:
//   promotionDrafts/{id}  admin-only working copy (every edit lands here)
//   promotions/{id}       published snapshot, written only by Publish
// Status is derived from the snapshot and the clock, never stored.
// Keep the rules in sync with the dashboard's model (both have tests).
// =============================================================================

export type PromotionStatus = 'draft' | 'scheduled' | 'active' | 'ended' | 'disabled' | 'archived';

export type PromotionDraft = {
  title: string;
  subtitle: string;
  description: string;
  ctaLabel: string;
  coverImageUrl: string;
  coverImagePath: string;
  startAt: number | null; // epoch ms
  endAt: number | null; // epoch ms
  priority: number;
  cityKeys: string[];
  eventIds: string[];
  archived: boolean;
  revision: number;
};

export type PublishedSnapshot = {
  published: boolean;
  startAt: number | null;
  endAt: number | null;
  version: number;
  sourceRevision: number;
  publishedAt: number | null;
  coverImagePath?: string;
};

export type AdminPromotion = { id: string; draft: PromotionDraft; published: PublishedSnapshot | null };

export const PROMOTION_LIMITS = {
  title: 80,
  subtitle: 140,
  description: 2000,
  ctaLabel: 30,
  events: 50,
  priorityMax: 999,
} as const;

export const STATUS_ORDER: PromotionStatus[] = ['active', 'scheduled', 'draft', 'disabled', 'ended', 'archived'];

export function emptyPromotionDraft(): PromotionDraft {
  return {
    title: '',
    subtitle: '',
    description: '',
    ctaLabel: '',
    coverImageUrl: '',
    coverImagePath: '',
    startAt: null,
    endAt: null,
    priority: 10,
    cityKeys: [SUPPORTED_CITIES[0].value],
    eventIds: [],
    archived: false,
    revision: 0,
  };
}

export function computePromotionStatus(draft: Pick<PromotionDraft, 'archived'> | null, published: PublishedSnapshot | null, now = Date.now()): PromotionStatus {
  if (draft?.archived) return 'archived';
  if (!published) return 'draft';
  if (published.published !== true || published.startAt === null || published.endAt === null) return 'disabled';
  if (now < published.startAt) return 'scheduled';
  if (now < published.endAt) return 'active';
  return 'ended';
}

export function hasUnpublishedChanges(draft: Pick<PromotionDraft, 'revision'> | null, published: PublishedSnapshot | null): boolean {
  if (!draft || !published || published.published !== true) return false;
  return (draft.revision || 0) !== (published.sourceRevision || 0);
}

// ---- Europe/Berlin wall time (DST-correct without Intl time zones) ---------
// CET (UTC+1) / CEST (UTC+2): summer time runs from 01:00 UTC on the last
// Sunday of March to 01:00 UTC on the last Sunday of October (EU rule).

function lastSundayUtc(year: number, monthIndex: number): number {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0));
  return Date.UTC(year, monthIndex, lastDay.getUTCDate() - lastDay.getUTCDay(), 1, 0, 0);
}

export function berlinOffsetMinutes(utcMs: number): number {
  const year = new Date(utcMs).getUTCFullYear();
  return utcMs >= lastSundayUtc(year, 2) && utcMs < lastSundayUtc(year, 9) ? 120 : 60;
}

export type BerlinParts = { year: number; month: number; day: number; hour: number; minute: number };

export function berlinPartsToMillis({ year, month, day, hour, minute }: BerlinParts): number | null {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const check = new Date(wall);
  if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day || hour > 23 || minute > 59) return null;
  let candidate = wall - berlinOffsetMinutes(wall - 60 * 60000) * 60000;
  candidate = wall - berlinOffsetMinutes(candidate) * 60000;
  return candidate;
}

export function millisToBerlinParts(ms: number): BerlinParts {
  const shifted = new Date(ms + berlinOffsetMinutes(ms) * 60000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

// 'YYYY-MM-DD' / 'HH:mm' strings in Berlin time — the format PickerField uses.
export function berlinDateString(ms: number | null): string {
  if (ms === null) return '';
  const p = millisToBerlinParts(ms);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

export function berlinTimeString(ms: number | null): string {
  if (ms === null) return '';
  const p = millisToBerlinParts(ms);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

export function berlinDateTimeToMillis(date: string, time: string): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const t = /^(\d{1,2}):(\d{2})$/.exec(time.trim() || '00:00');
  if (!d || !t) return null;
  return berlinPartsToMillis({ year: Number(d[1]), month: Number(d[2]), day: Number(d[3]), hour: Number(t[1]), minute: Number(t[2]) });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatBerlin(ms: number | null): string {
  if (ms === null) return '—';
  const p = millisToBerlinParts(ms);
  return `${p.day} ${MONTHS[p.month - 1]} ${p.year}, ${pad(p.hour)}:${pad(p.minute)}`;
}

// ---- Events -----------------------------------------------------------------

export type EventVerdict = { eligible: boolean; reason: string; endsAt: number | null };

// What the app will actually show: the same gate the feed uses, plus the
// StudiZone exclusion (StudiZone-only events never reach the Main App feed).
export function classifyPromotionEvent(event: TonyteActivity | undefined, now = Date.now()): EventVerdict {
  if (!event) return { eligible: false, reason: 'Not available (ended, deleted or not visible)', endsAt: null };
  if (event.visibility === 'private') return { eligible: false, reason: 'Private event', endsAt: null };
  if (event.eventVisibility === 'studizone' || isStudiZoneActivity(event)) return { eligible: false, reason: 'StudiZone-only event', endsAt: null };
  const endsAt = parseActivityDate(event).endAt?.getTime() ?? null;
  if (!isEventAvailableForDiscovery(event, new Date(now))) return { eligible: false, reason: 'Ended or not live', endsAt };
  return { eligible: true, reason: '', endsAt };
}

// ---- Validation -------------------------------------------------------------

const len = (value: string) => value.trim().length;

// Events that have definitely ended. They can never show again (the feed
// already drops them), so editors prune them from the list on save/publish.
export function isEventKnownEnded(event: TonyteActivity | undefined, now = Date.now()): boolean {
  if (!event) return false;
  const endsAt = parseActivityDate(event).endAt?.getTime();
  return typeof endsAt === 'number' && endsAt < now;
}

export function pruneEndedEvents(eventIds: string[], eventsById: Map<string, TonyteActivity>, now = Date.now()): string[] {
  return eventIds.filter(id => !isEventKnownEnded(eventsById.get(id), now));
}

// errors block; warnings are real problems worth a look; notes are
// informational (expected behaviour, nothing to fix).
export function validatePromotion(
  draft: PromotionDraft,
  { intent = 'save', now = Date.now(), eventsById = new Map<string, TonyteActivity>() }: { intent?: 'save' | 'publish'; now?: number; eventsById?: Map<string, TonyteActivity> } = {},
): { errors: string[]; warnings: string[]; notes: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const notes: string[] = [];
  if (!len(draft.title)) errors.push('Title is required.');
  if (len(draft.title) > PROMOTION_LIMITS.title) errors.push(`Title must be at most ${PROMOTION_LIMITS.title} characters.`);
  if (len(draft.subtitle) > PROMOTION_LIMITS.subtitle) errors.push(`Subtitle must be at most ${PROMOTION_LIMITS.subtitle} characters.`);
  if (len(draft.description) > PROMOTION_LIMITS.description) errors.push(`Description must be at most ${PROMOTION_LIMITS.description} characters.`);
  if (len(draft.ctaLabel) > PROMOTION_LIMITS.ctaLabel) errors.push(`Button label must be at most ${PROMOTION_LIMITS.ctaLabel} characters.`);
  if (!Number.isInteger(draft.priority) || draft.priority < 0 || draft.priority > PROMOTION_LIMITS.priorityMax) {
    errors.push(`Priority must be a whole number from 0 to ${PROMOTION_LIMITS.priorityMax}.`);
  }
  if (draft.eventIds.length > PROMOTION_LIMITS.events) errors.push(`At most ${PROMOTION_LIMITS.events} events per promotion.`);
  const { startAt, endAt } = draft;
  if (startAt !== null && endAt !== null && endAt <= startAt) errors.push('The end must be after the start.');

  if (intent === 'publish') {
    if (startAt === null) errors.push('Start date and time are required to publish.');
    if (endAt === null) errors.push('End date and time are required to publish.');
    if (endAt !== null && endAt <= now) errors.push('The end is in the past — this promotion would never be shown.');
    if (draft.coverImageUrl && !/^https:\/\//.test(draft.coverImageUrl)) errors.push('The cover image link is invalid — upload it again.');
    if (!draft.coverImageUrl) notes.push('No cover image — the card shows a gradient placeholder until you add one.');
    if (!draft.eventIds.length) errors.push('Select at least one event.');
    let eligible = 0;
    let ended = 0;
    let latestEnd = 0;
    draft.eventIds.forEach(id => {
      const event = eventsById.get(id);
      if (isEventKnownEnded(event, now)) {
        ended += 1;
        return;
      }
      const verdict = classifyPromotionEvent(event, now);
      const label = event?.title ? `“${event.title}”` : `Event ${id}`;
      if (!verdict.eligible) {
        warnings.push(`${label} won’t be shown: ${verdict.reason}.`);
        return;
      }
      eligible += 1;
      if (verdict.endsAt !== null) latestEnd = Math.max(latestEnd, verdict.endsAt);
      const eventCity = normalizeCityKey(event?.cityKey || event?.city);
      if (draft.cityKeys.length && eventCity && !draft.cityKeys.includes(eventCity)) {
        warnings.push(`${label} is in ${event?.city || eventCity}, which this promotion doesn’t target.`);
      }
    });
    if (ended) notes.push(`${ended} event${ended === 1 ? ' has' : 's have'} already ended and will be removed when you save.`);
    if (draft.eventIds.length && !eligible) errors.push('None of the selected events can be shown — users would see nothing.');
    if (eligible && endAt !== null && latestEnd && latestEnd < endAt) {
      notes.push(`Events drop out automatically once they end. The collection hides itself after the last one (${formatBerlin(latestEnd)}), even before its end date.`);
    }
  }
  return { errors, warnings, notes };
}

export function promotionContent(draft: PromotionDraft) {
  return {
    title: draft.title.trim(),
    subtitle: draft.subtitle.trim(),
    description: draft.description.trim(),
    ctaLabel: draft.ctaLabel.trim(),
    coverImageUrl: draft.coverImageUrl || '',
    coverImagePath: draft.coverImagePath || '',
    priority: Number.isInteger(draft.priority) ? draft.priority : 10,
    cityKeys: Array.from(new Set(draft.cityKeys.map(c => normalizeCityKey(c)))),
    eventIds: Array.from(new Set(draft.eventIds)).slice(0, PROMOTION_LIMITS.events),
  };
}

export function moveId(ids: string[], id: string, direction: -1 | 1): string[] {
  const index = ids.indexOf(id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ids.length) return ids;
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
